/**
 * RoomManagement.js - Admin Room Management
 * 
 * Allows admins to:
 * - View all rooms with ratio and age range info
 * - Create new rooms with optional ratio and age range
 * - Edit room details
 * - Delete rooms
 * - Reorder rooms (move up/down)
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import dataStore from '../data/dataStore';

// Ratio options for dropdown - null means no ratio set
const RATIO_OPTIONS = [
  { value: '', label: 'No Ratio' },
  { value: 4, label: '4:1' },
  { value: 5, label: '5:1' },
  { value: 6, label: '6:1' },
  { value: 7, label: '7:1' },
  { value: 8, label: '8:1' },
  { value: 10, label: '10:1' },
  { value: 12, label: '12:1' }
];

// Common age range presets
const AGE_PRESETS = [
  { label: '6w-12m', minMonths: 1.5, maxMonths: 12, displayText: '6w-12m' },
  { label: '12m-18m', minMonths: 12, maxMonths: 18, displayText: '12m-18m' },
  { label: '18m-27m', minMonths: 18, maxMonths: 27, displayText: '18m-27m' },
  { label: '27m-3y', minMonths: 27, maxMonths: 36, displayText: '27m-3y' },
  { label: '3y-4y', minMonths: 36, maxMonths: 48, displayText: '3y-4y' },
  { label: '4y-K', minMonths: 48, maxMonths: 72, displayText: '4y-K' },
  { label: 'Custom', minMonths: null, maxMonths: null, displayText: '' }
];

function RoomManagement() {
  const { theme, colors, darkMode } = useTheme();
  const { user: currentUser } = useAuth();
  
  const [rooms, setRooms] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newRoom, setNewRoom] = useState({ 
    name: '', 
    shortName: '', 
    ratio: '',
    agePreset: '',
    ageRange: null,
    showOnStudentSchedule: true,
    showOnTeacherSchedule: true,
    isUtility: false
  });
  const [editingRoom, setEditingRoom] = useState(null);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    shortName: '', 
    ratio: '',
    agePreset: '',
    ageRange: null,
    showOnStudentSchedule: true,
    showOnTeacherSchedule: true,
    isUtility: false
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Load rooms
  const loadRooms = () => {
    setRooms([...dataStore.getRooms()]);
  };

  useEffect(() => {
    loadRooms();
  }, []);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Handle age preset change for create form
  const handleAgePresetChange = (presetLabel, isEdit = false) => {
    const preset = AGE_PRESETS.find(p => p.label === presetLabel);
    if (preset && preset.minMonths !== null) {
      const ageRange = {
        minMonths: preset.minMonths,
        maxMonths: preset.maxMonths,
        displayText: preset.displayText
      };
      if (isEdit) {
        setEditForm({ ...editForm, agePreset: presetLabel, ageRange });
      } else {
        setNewRoom({ ...newRoom, agePreset: presetLabel, ageRange });
      }
    } else {
      if (isEdit) {
        setEditForm({ ...editForm, agePreset: presetLabel, ageRange: null });
      } else {
        setNewRoom({ ...newRoom, agePreset: presetLabel, ageRange: null });
      }
    }
  };

  // Create new room
  const handleCreate = async () => {
    setError('');
    if (!newRoom.name.trim()) {
      setError('Room name is required');
      return;
    }
    try {
      await dataStore.createRoom({
        name: newRoom.name.trim(),
        shortName: newRoom.shortName.trim() || newRoom.name.substring(0, 6),
        ratio: newRoom.ratio === '' ? null : parseInt(newRoom.ratio),
        ageRange: newRoom.ageRange,
        showOnStudentSchedule: newRoom.showOnStudentSchedule,
        showOnTeacherSchedule: newRoom.showOnTeacherSchedule,
        isUtility: newRoom.isUtility
      }, currentUser);
      setNewRoom({ name: '', shortName: '', ratio: '', agePreset: '', ageRange: null, showOnStudentSchedule: true, showOnTeacherSchedule: true, isUtility: false });
      setShowCreateForm(false);
      loadRooms();
      setMessage('Room created successfully!');
    } catch (err) {
      console.error('Error creating room:', err);
      setError(err.message);
    }
  };

  // Start editing a room
  const startEdit = (room) => {
    // Find matching preset
    let matchingPreset = '';
    if (room.ageRange) {
      const preset = AGE_PRESETS.find(p => 
        p.minMonths === room.ageRange.minMonths && 
        p.maxMonths === room.ageRange.maxMonths
      );
      matchingPreset = preset ? preset.label : 'Custom';
    }
    
    setEditingRoom(room.id);
    setEditForm({ 
      name: room.name, 
      shortName: room.shortName,
      ratio: room.ratio || '',
      agePreset: matchingPreset,
      ageRange: room.ageRange || null,
      showOnStudentSchedule: room.showOnStudentSchedule !== false,
      showOnTeacherSchedule: room.showOnTeacherSchedule !== false,
      isUtility: room.isUtility || false
    });
  };

  // Save room edit
  const handleSaveEdit = async (roomId) => {
    try {
      await dataStore.updateRoom(roomId, {
        name: editForm.name.trim(),
        shortName: editForm.shortName.trim(),
        ratio: editForm.ratio === '' ? null : parseInt(editForm.ratio),
        ageRange: editForm.ageRange,
        showOnStudentSchedule: editForm.showOnStudentSchedule,
        showOnTeacherSchedule: editForm.showOnTeacherSchedule,
        isUtility: editForm.isUtility
      }, currentUser);
      setEditingRoom(null);
      loadRooms();
      setMessage('Room updated!');
    } catch (err) {
      console.error('Error updating room:', err);
      setError(err.message);
    }
  };

  // Delete room
  const handleDelete = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room? This may affect existing schedules.')) {
      try {
        await dataStore.deleteRoom(roomId, currentUser);
        loadRooms();
        setMessage('Room deleted');
      } catch (err) {
        console.error('Error deleting room:', err);
        setError(err.message);
      }
    }
  };

  // Move room up in the list
  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newRooms = [...rooms];
    [newRooms[index - 1], newRooms[index]] = [newRooms[index], newRooms[index - 1]];
    await dataStore.updateRoomOrder(newRooms.map(r => r.id), currentUser);
    loadRooms();
  };

  // Move room down in the list
  const handleMoveDown = async (index) => {
    if (index === rooms.length - 1) return;
    const newRooms = [...rooms];
    [newRooms[index], newRooms[index + 1]] = [newRooms[index + 1], newRooms[index]];
    await dataStore.updateRoomOrder(newRooms.map(r => r.id), currentUser);
    loadRooms();
  };

  // =============================================
  // STYLES
  // =============================================
  const containerStyle = {
    padding: '20px',
    maxWidth: '1100px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: '25px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    marginBottom: '20px'
  };

  const titleStyle = {
    color: theme.text,
    marginTop: 0
  };

  const buttonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: darkMode ? '#fff' : '#333',
    marginRight: '10px',
    fontSize: '0.9rem'
  };

  const inputStyle = {
    padding: '10px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.background,
    color: theme.text,
    marginRight: '10px',
    marginBottom: '10px'
  };

  const selectStyle = {
    ...inputStyle,
    minWidth: '150px'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
  };

  const thStyle = {
    padding: '12px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#333',
    textAlign: 'left',
    fontSize: '0.9rem',
    fontWeight: '600'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: `1px solid ${theme.border}`,
    color: theme.text,
    verticalAlign: 'middle'
  };

  const messageStyle = {
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    textAlign: 'center'
  };

  const badgeStyle = {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: '600',
    display: 'inline-block'
  };

  const formRowStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'flex-end',
    marginBottom: '15px'
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };

  const labelStyle = {
    fontSize: '0.8rem',
    color: theme.textSecondary,
    fontWeight: '500'
  };

  const orderButtonStyle = {
    padding: '4px 8px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.backgroundSecondary,
    color: theme.text,
    cursor: 'pointer',
    fontSize: '0.9rem',
    marginRight: '4px'
  };

  const disabledOrderButtonStyle = {
    ...orderButtonStyle,
    opacity: 0.3,
    cursor: 'not-allowed'
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={titleStyle}>🏠 Room Management</h1>
          <button
            style={{ ...buttonStyle, backgroundColor: colors.green, color: darkMode ? '#fff' : '#333', fontWeight: '600' }}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ Add Room'}
          </button>
        </div>

        {/* Messages */}
        {message && (
          <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#fff' : '#333', fontWeight: '600' }}>
            {message}
          </div>
        )}
        {error && (
          <div style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#fff' : '#333' }}>
            {error}
          </div>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <div style={{ marginTop: '20px', marginBottom: '20px', padding: '20px', backgroundColor: theme.backgroundSecondary, borderRadius: '8px' }}>
            <h3 style={{ color: theme.text, marginTop: 0, marginBottom: '15px' }}>Create New Room</h3>
            
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Room Name *</label>
                <input
                  type="text"
                  placeholder="e.g., Infant 1"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                  style={{ ...inputStyle, width: '180px', marginBottom: 0 }}
                />
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Short Name</label>
                <input
                  type="text"
                  placeholder="e.g., Inf1"
                  value={newRoom.shortName}
                  onChange={(e) => setNewRoom({ ...newRoom, shortName: e.target.value })}
                  style={{ ...inputStyle, width: '100px', marginBottom: 0 }}
                />
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Ratio (students:teacher)</label>
                <select
                  value={newRoom.ratio}
                  onChange={(e) => setNewRoom({ ...newRoom, ratio: e.target.value })}
                  style={{ ...selectStyle, marginBottom: 0 }}
                >
                  {RATIO_OPTIONS.map(opt => (
                    <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Age Range</label>
                <select
                  value={newRoom.agePreset}
                  onChange={(e) => handleAgePresetChange(e.target.value, false)}
                  style={{ ...selectStyle, marginBottom: 0 }}
                >
                  <option value="">-- Select --</option>
                  {AGE_PRESETS.map(preset => (
                    <option key={preset.label} value={preset.label}>{preset.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Visibility Settings */}
            <div style={{ ...formRowStyle, marginBottom: '15px' }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Show on Schedules</label>
                <div style={{ display: 'flex', gap: '20px', marginTop: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: theme.text, fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={newRoom.showOnStudentSchedule}
                      onChange={(e) => setNewRoom({ ...newRoom, showOnStudentSchedule: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: colors.blue }}
                    />
                    Student Schedule
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: theme.text, fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={newRoom.showOnTeacherSchedule}
                      onChange={(e) => setNewRoom({ ...newRoom, showOnTeacherSchedule: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: colors.blue }}
                    />
                    Teacher Schedule
                  </label>
                </div>
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Room Type</label>
                <div style={{ display: 'flex', gap: '20px', marginTop: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: theme.text, fontSize: '0.9rem' }}>
                    <input
                      type="checkbox"
                      checked={newRoom.isUtility}
                      onChange={(e) => setNewRoom({ ...newRoom, isUtility: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: colors.purple }}
                    />
                    Utility Room (Break/Lunch coverage - appears on all printed room cards)
                  </label>
                </div>
              </div>
            </div>
            
            <button
              style={{ ...buttonStyle, backgroundColor: colors.blue }}
              onClick={handleCreate}
            >
              Create Room
            </button>
            
            <p style={{ color: theme.textSecondary, fontSize: '0.85rem', marginTop: '10px', marginBottom: 0 }}>
              * Required. Short name appears in compact views. Ratio is optional and used for staffing validation.
            </p>
          </div>
        )}

        {/* Rooms Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '60px', textAlign: 'center' }}>Order</th>
                <th style={thStyle}>Room</th>
                <th style={thStyle}>Short Name</th>
                <th style={thStyle}>Ratio</th>
                <th style={thStyle}>Age Range</th>
                <th style={thStyle}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Visibility
                    <span 
                      title="Controls which schedule pages this room appears on. Uncheck 'Students' to hide from Student Schedule, uncheck 'Teachers' to hide from Teacher Schedule."
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'help'
                      }}
                    >i</span>
                  </span>
                </th>
                <th style={{ ...thStyle, textAlign: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    Utility
                    <span 
                      title="Utility rooms are for staff coverage, not classrooms. They appear as a section on every printed room card."
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'help'
                      }}
                    >i</span>
                  </span>
                </th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room, index) => (
                <tr key={room.id}>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      style={index === 0 ? disabledOrderButtonStyle : orderButtonStyle}
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      style={index === rooms.length - 1 ? disabledOrderButtonStyle : orderButtonStyle}
                      onClick={() => handleMoveDown(index)}
                      disabled={index === rooms.length - 1}
                      title="Move Down"
                    >
                      ↓
                    </button>
                  </td>
                  <td style={tdStyle}>
                    {editingRoom === room.id ? (
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        style={{ ...inputStyle, marginBottom: 0, width: '120px' }}
                      />
                    ) : (
                      <strong>{room.name}</strong>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editingRoom === room.id ? (
                      <input
                        type="text"
                        value={editForm.shortName}
                        onChange={(e) => setEditForm({ ...editForm, shortName: e.target.value })}
                        style={{ ...inputStyle, marginBottom: 0, width: '70px' }}
                      />
                    ) : (
                      <span style={{ ...badgeStyle, backgroundColor: colors.blue, color: darkMode ? '#fff' : '#333' }}>
                        {room.shortName}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editingRoom === room.id ? (
                      <select
                        value={editForm.ratio}
                        onChange={(e) => setEditForm({ ...editForm, ratio: e.target.value })}
                        style={{ ...selectStyle, marginBottom: 0, minWidth: '90px' }}
                      >
                        {RATIO_OPTIONS.map(opt => (
                          <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : room.ratio ? (
                      <span style={{ ...badgeStyle, backgroundColor: colors.purple, color: darkMode ? '#fff' : '#333' }}>
                        {room.ratio}:1
                      </span>
                    ) : (
                      <span style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>Not set</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editingRoom === room.id ? (
                      <select
                        value={editForm.agePreset}
                        onChange={(e) => handleAgePresetChange(e.target.value, true)}
                        style={{ ...selectStyle, marginBottom: 0, minWidth: '100px' }}
                      >
                        <option value="">-- None --</option>
                        {AGE_PRESETS.map(preset => (
                          <option key={preset.label} value={preset.label}>{preset.label}</option>
                        ))}
                      </select>
                    ) : (
                      room.ageRange ? (
                        <span style={{ ...badgeStyle, backgroundColor: colors.orange, color: darkMode ? '#fff' : '#333' }}>
                          {room.ageRange.displayText}
                        </span>
                      ) : (
                        <span style={{ color: theme.textSecondary, fontSize: '0.85rem' }}>Not set</span>
                      )
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editingRoom === room.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: theme.text, fontSize: '0.8rem' }}>
                          <input
                            type="checkbox"
                            checked={editForm.showOnStudentSchedule}
                            onChange={(e) => setEditForm({ ...editForm, showOnStudentSchedule: e.target.checked })}
                            style={{ width: '14px', height: '14px', accentColor: colors.blue }}
                          />
                          Students
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: theme.text, fontSize: '0.8rem' }}>
                          <input
                            type="checkbox"
                            checked={editForm.showOnTeacherSchedule}
                            onChange={(e) => setEditForm({ ...editForm, showOnTeacherSchedule: e.target.checked })}
                            style={{ width: '14px', height: '14px', accentColor: colors.blue }}
                          />
                          Teachers
                        </label>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.8rem' }}>
                        <span style={{ color: room.showOnStudentSchedule !== false ? colors.green : theme.textSecondary }}>
                          {room.showOnStudentSchedule !== false ? '✔' : '✗'} Students
                        </span>
                        <span style={{ color: room.showOnTeacherSchedule !== false ? colors.green : theme.textSecondary }}>
                          {room.showOnTeacherSchedule !== false ? '✔' : '✗'} Teachers
                        </span>
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {editingRoom === room.id ? (
                      <input
                        type="checkbox"
                        checked={editForm.isUtility}
                        onChange={(e) => setEditForm({ ...editForm, isUtility: e.target.checked })}
                        style={{ width: '18px', height: '18px', accentColor: colors.purple, cursor: 'pointer' }}
                      />
                    ) : (
                      room.isUtility ? (
                        <span style={{ 
                          backgroundColor: colors.purple, 
                          color: darkMode ? '#fff' : '#333', 
                          padding: '3px 8px', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          UTILITY
                        </span>
                      ) : (
                        <span style={{ color: theme.textSecondary }}>—</span>
                      )
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {editingRoom === room.id ? (
                      <>
                        <button
                          style={{ ...buttonStyle, backgroundColor: colors.green, padding: '6px 10px', color: darkMode ? '#fff' : '#333', fontWeight: '600' }}
                          onClick={() => handleSaveEdit(room.id)}
                        >
                          Save
                        </button>
                        <button
                          style={{ ...buttonStyle, backgroundColor: theme.textSecondary, padding: '6px 10px' }}
                          onClick={() => setEditingRoom(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          style={{ ...buttonStyle, backgroundColor: colors.blue, padding: '6px 12px' }}
                          onClick={() => startEdit(room)}
                        >
                          Edit
                        </button>
                        <button
                          style={{ ...buttonStyle, backgroundColor: colors.red, padding: '6px 12px', color: '#fff' }}
                          onClick={() => handleDelete(room.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rooms.length === 0 && (
          <p style={{ textAlign: 'center', color: theme.textSecondary, padding: '20px' }}>
            No rooms yet. Click "Add Room" to create one.
          </p>
        )}
      </div>
    </div>
  );
}

export default RoomManagement;

